if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.giftAddInFlight = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const leadTimeInput = this.form.elements.namedItem('properties[_lead_time]');
        const secondImageInput = this.form.elements.namedItem('properties[_second_image]');
        const leadTime = leadTimeInput?.value || this.dataset.leadTime;
        const secondImage = secondImageInput?.value || this.dataset.secondImage;
        if (!formData.get('properties[_lead_time]') && leadTime) formData.append('properties[_lead_time]', leadTime);
        if (!formData.get('properties[_second_image]') && secondImage) formData.append('properties[_second_image]', secondImage);
        // Week picker's ISO dates as hidden line-item properties: language-
        // independent source for internal processing (underscore-prefixed,
        // so invisible in cart/checkout; Plumsail ignores unknown fields).
        const weekStart = document.querySelector('[data-week-start-input]')?.value;
        const weekEnd = document.querySelector('[data-week-end-input]')?.value;
        if (!formData.get('properties[_week_start]') && weekStart) formData.append('properties[_week_start]', weekStart);
        if (!formData.get('properties[_week_end]') && weekEnd) formData.append('properties[_week_end]', weekEnd);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const primaryVariantId = formData.get('id');
            this.addGiftVariantIfNeeded(response, primaryVariantId).then((finalResponse) => {
              if (!this.error && this.cart)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: primaryVariantId,
                  cartData: finalResponse,
                });
              this.error = false;
              if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(finalResponse);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(finalResponse);
              }
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      addGiftVariantIfNeeded(response, primaryVariantId) {
        const giftVariantId = this.dataset.giftVariantId;
        if (!giftVariantId) return Promise.resolve(response);
        if (String(primaryVariantId) === String(giftVariantId)) return Promise.resolve(response);
        if (this.giftAddInFlight) return Promise.resolve(response);

        this.giftAddInFlight = true;

        return fetch(`${routes.cart_url}.js`, {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((cartResponse) => cartResponse.json())
          .then((cart) => {
            const alreadyInCart = Array.isArray(cart.items)
              ? cart.items.some((item) => String(item.id) === String(giftVariantId))
              : false;
            if (alreadyInCart) return response;

            const giftConfig = fetchConfig('javascript');
            giftConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete giftConfig.headers['Content-Type'];

            const giftFormData = new FormData();
            giftFormData.append('id', giftVariantId);
            giftFormData.append('quantity', 1);
            if (this.cart) {
              giftFormData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              giftFormData.append('sections_url', window.location.pathname);
            }
            giftConfig.body = giftFormData;

            return fetch(`${routes.cart_add_url}`, giftConfig)
              .then((giftResponse) => giftResponse.json())
              .then((giftResponse) => {
                return giftResponse.status ? response : giftResponse;
              });
          })
          .catch((e) => {
            console.warn('Failed to auto-add gift variant', e);
            return response;
          })
          .finally(() => {
            this.giftAddInFlight = false;
          });
      }
    }
  );
}
